import React, { useEffect, useRef, useState } from "react";
import api from "../../chat/api";
import Shell from "./CopilotCardShell";
import ShowDocumentButton from "./ShowDocumentButton";
import { draftNode } from "../../../services/sectionDraft";

/* One section being written, live.
 *
 * THE BAR IS REAL. The whole section - intro and every still-empty subsection -
 * is one request, and the server reports its own progress through it. The fill
 * is that number, not a timer. A lying bar is worse than an honest spinner.
 *
 * THE TEXT TAIL IS THE PROOF. A bar can be believed or not; watching the last
 * clause of the sentence being written cannot be faked. It is also the cheapest
 * possible version of the document panel, which lands next — the same deltas
 * feed both, so nothing here is thrown away.
 *
 * WHY THE OUTLINE IS RE-FETCHED PER SECTION. The card mounts once per section,
 * and re-reading is one lean GET. It means a section removed on the project
 * screen mid-flow cannot be written from a snapshot this card is still holding.
 */

const clamp = (n) => Math.max(0, Math.min(100, n));

/* How much of the live draft is kept for the preview. Enough to fill the window
 * several times over so the text scrolls past rather than appearing and
 * vanishing; bounded because a section runs to thousands of words and the
 * document panel is where the whole draft lives. */
const TAIL_CHARS = 1500;

const ProposalWritingCard = ({ message, onDone, onFailed, docOpen, onOpenDoc }) => {
  /* `instruction` is set only by a rewrite, and it applies to every node in the
   * section — the note was about the section as a whole ("make the SLA
   * paragraph firmer"), not about whichever subsection happens to contain it. */
  const { projectId, sectionIdx = 0, instruction = "" } = message || {};

  const [section, setSection] = useState(null);
  const [subCount, setSubCount] = useState(0);
  /* Every stage the server has reported, in order, rather than only the latest.
   *
   * The server narrates its own work — reading the RFP, shaping the plan,
   * searching the knowledge base, naming the documents the evidence came from,
   * then drafting. All of that used to be written into ONE line that each new
   * stage overwrote, so a minute of real work left no trace and the only thing
   * to watch was a bar. Keeping the list turns the same frames into an account
   * of what was done and why, which is the thing a bid team actually wants to
   * see before trusting a section. */
  const [steps, setSteps] = useState([]);
  const [pct, setPct] = useState(0);
  const [tail, setTail] = useState("");
  const [words, setWords] = useState(0);
  const [error, setError] = useState(null);
  /* Collapsed by default. Four lines is enough to see that real sentences are
   * being written; anyone who wants to READ the draft has the document panel.
   * Expanding is for the middle case — following closely without leaving. */
  const [expanded, setExpanded] = useState(false);

  /* Started-once guard. React 18 mounts effects twice in development, and the
   * second run would fire a whole section's worth of drafting requests at the
   * model — expensive, and the two runs would interleave their progress. */
  const startedRef = useRef(false);
  const wordsRef = useRef(0);
  const liveRef = useRef(null);
  const tailRef = useRef(null);

  useEffect(() => {
    if (startedRef.current) return undefined;
    startedRef.current = true;
    let alive = true;

    (async () => {
      let toc;
      try {
        const res = await api.get(`/api/v1/rfp/folders/${projectId}/proposal/outline/confirm/`);
        toc = Array.isArray(res?.data?.toc) ? res.data.toc : [];
      } catch {
        if (alive) setError("Couldn't read the outline.");
        return;
      }

      const sec = toc[sectionIdx];
      if (!sec) {
        // Past the end: every section is written. Not an error — the caller
        // asked for a section that no longer exists because the work is done.
        if (alive) onDone?.({ finished: true });
        return;
      }
      if (!alive) return;

      setSection(sec);
      setSubCount((sec.children || []).length);

      /* ONE request for the whole section.
       *
       * Passing the section id drafts its intro AND every still-empty
       * subsection in a single LLM call - the server builds one prompt for the
       * family (see _draft_section_family). This card used to loop over the
       * nodes and fire a request each, which was six round trips for a
       * five-subsection section AND worse prose: each child was written in its
       * own prompt, so siblings could not see one another and repeated the same
       * ground. One call is both faster and more coherent.
       *
       * No client-side skip either. The server drops children that already have
       * a response and returns early if the whole family is written, so a resume
       * is handled where the data actually lives. */
      const run = draftNode({
        folderId: projectId,
        targetId: sec.id,
        isSubsection: false,
        instruction,
        onProgress: (d) => {
          if (!alive) return;
          if (typeof d.progress === "number") setPct(clamp(d.progress));
          if (!d.message) return;
          /* Appended, not replaced — but only when it is genuinely new. The
           * same stage is re-sent on every poll, and a list that grew by one
           * row per frame would be the same sentence forty times. */
          setSteps((prev) =>
            prev[prev.length - 1] === d.message ? prev : [...prev, d.message]
          );
        },
        onStream: (delta) => {
          if (!alive) return;
          wordsRef.current += (delta.match(/\S+/g) || []).length;
          // Surfaced as well as counted: a number climbing past three hundred is
          // the cheapest proof that something is being produced.
          setWords(wordsRef.current);
          setTail((prev) => (prev + delta).slice(-TAIL_CHARS));
        },
      });
      liveRef.current = run;

      const out = await run.promise;
      if (!alive) return;
      if (!out.ok) {
        if (out.error !== "cancelled") setError(out.error);
        return;
      }
      setPct(100);

      /* Held at 100 before handing over, so the fill is seen to finish rather
       * than being replaced mid-stride. The handoff calls this beat deliberate
       * and it is — without it the card looks like it was interrupted. */
      await new Promise((r) => setTimeout(r, 420));
      if (!alive) return;
      onDone?.({
        sectionTitle: sec.title,
        subsections: (sec.children || []).length,
        words: wordsRef.current,
        // Which node was written, so the collapsed record can fetch the text
        // back on demand rather than carrying thousands of words into a chat row.
        sectionId: sec.id,
        // The outline is the only thing that knows how many sections there are,
        // and this card is what read it — so the "last one" wording is decided
        // here rather than by the page counting separately and disagreeing.
        isLast: sectionIdx >= toc.length - 1,
        // How many sections in total, so the review card can say "3 of 12"
        // instead of a bare "Section 3" that answers nothing about progress.
        totalSections: toc.length,
        // Naming what Approve starts, so agreeing to it is an informed click.
        nextTitle: toc[sectionIdx + 1]?.title || "",
        // Cleared so the next section is a first draft, not a re-run of the
        // note that was meant for this one.
        instruction: "",
      });
    })();

    return () => {
      alive = false;
      liveRef.current?.cancel();
    };
    // Deliberately mount-only: sectionIdx changes come with a new key from the
    // page, which remounts this card rather than restarting a loop mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) onFailed?.(error);
  }, [error, onFailed]);

  /* Keep the newest words in view, but only while the reader is already at the
   * bottom. Collapsed that is always true. Expanded it matters: someone who has
   * scrolled up to re-read must not have it yanked away four times a second. */
  useEffect(() => {
    const el = tailRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom) el.scrollTop = el.scrollHeight;
  }, [tail, expanded]);


  return (
    <Shell
      icon={
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      }
      title={section ? `Writing ${section.title}` : "Writing"}
      meta={
        <span className="flex items-center gap-2 text-[12px] tabular-nums text-[#6B6472]">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#A04A97] opacity-70 motion-reduce:animate-none" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#A04A97]" />
          </span>
          {/* Swaps once there is something to count: "312 words" says how much
              exists, which beats "4 subsections" the moment prose lands. */}
          {words
            ? `${words.toLocaleString()} word${words === 1 ? "" : "s"}`
            : subCount
            ? `${subCount} subsection${subCount === 1 ? "" : "s"}`
            : "drafting"}
        </span>
      }
    >
      <div className="px-4 py-3.5">
        {error ? (
          <p role="alert" className="text-[13px] text-[#B91C1C]">
            {error}
          </p>
        ) : (
          <>
            {/* The work, one step at a time.
             *
             * A bar answers "how far" and nothing else, and for a minute of
             * model time that is not enough to keep anyone watching — nor is it
             * what a bid team needs. They need to know the section was written
             * from THEIR documents: which ones were searched, what evidence came
             * back, and that it was drafted from it. The server already says all
             * of that; it was being thrown away one line at a time.
             *
             * Finished steps keep their text and take a tick. The live one keeps
             * the pulse. Nothing is removed, so by the end the card holds the
             * whole account of how the section came to exist. */}
            <ol className="space-y-1.5">
              {(steps.length ? steps : ["Starting…"]).map((s, i) => {
                const live = i === steps.length - 1;
                return (
                  <li
                    key={`${i}-${s}`}
                    className="flex items-start gap-2 text-[13px] leading-snug animate-copilot-rise motion-reduce:animate-none"
                  >
                    <span className="mt-[5px] flex h-2 w-2 flex-none items-center justify-center">
                      {live ? (
                        <>
                          <span className="absolute inline-flex h-2 w-2 animate-ping rounded-full bg-[#A04A97] opacity-70 motion-reduce:animate-none" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#A04A97]" />
                        </>
                      ) : (
                        <svg aria-hidden="true" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#1F7D38" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </span>
                    <span className={live ? "text-[#18101C]" : "text-[#6B6472]"}>{s}</span>
                  </li>
                );
              })}
            </ol>

            {/* A hairline, not a bar. The steps say what is happening; this only
                says roughly how much is left, so it sits under them at 2px
                rather than leading the card. */}
            <div
              className="mt-3 h-[2px] w-full overflow-hidden rounded-full bg-[#18101C]/[0.07]"
              role="progressbar"
              aria-valuenow={Math.round(pct)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={section ? `Writing ${section.title}` : "Writing"}
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#A04A97] to-[#C86B4F] transition-[width] duration-500 ease-sidebar motion-reduce:transition-none"
                style={{ width: `${pct}%` }}
              />
            </div>

            {/* The draft itself, as it is written.
             *
             * This used to be ONE truncated line, which proved a request was
             * alive and nothing more. A section takes minutes; that is a long
             * time to be shown a progress bar and no product.
             *
             * Two literal class strings rather than an interpolated height —
             * Tailwind emits only the names it can SEE in source. The top fade
             * belongs to the COLLAPSED state only; expanded, the reader is here
             * to read, and dimming the line they are reading toward is a veil
             * over the text.
             *
             * aria-hidden: the document panel renders this text properly, and
             * announcing every token would make the page unusable with a screen
             * reader. The step list above carries the state instead, in whole
             * sentences that are worth announcing. */}
            {tail && (
              <div
                ref={tailRef}
                aria-hidden="true"
                className={
                  expanded
                    ? "mt-2 h-[15rem] overflow-y-auto whitespace-pre-wrap break-words text-[12.5px] leading-[1.6] text-[#57515F]"
                    : "mt-2 h-[4.75rem] overflow-hidden whitespace-pre-wrap break-words text-[12.5px] leading-[1.6] text-[#6B6472] [mask-image:linear-gradient(to_bottom,transparent,#000_1.5rem)]"
                }
              >
                {tail}
              </div>
            )}

            {/* The two things to do while a section writes: see more of it here,
                or go and read it properly. The document link was a bare icon in
                the header strip — right size, wrong place. It carries its label
                here, and only here, so the card never shows it twice. */}
            {(tail || !docOpen) && (
              <div className="mt-2.5 flex items-center justify-between gap-3">
                {tail ? (
                  <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    aria-expanded={expanded}
                    className="inline-flex items-center gap-1 text-[12px] font-medium text-[#6B6472] transition-colors duration-150 hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
                  >
                    <svg
                      aria-hidden="true"
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`transition-transform duration-200 motion-reduce:transition-none ${
                        expanded ? "rotate-180" : ""
                      }`}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                    {expanded ? "Collapse" : "Expand"}
                  </button>
                ) : (
                  <span />
                )}
                <ShowDocumentButton open={docOpen} onOpen={onOpenDoc} label="Open the proposal" />
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
};

export default ProposalWritingCard;
